import moment from 'moment';
import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CalendarEventType } from '@/components/event-calendar/types/CalendarEventType';
import { useGetCustomerByIdQuery } from '@/slices/customerSlice';

export const ADD_EVENT_PARAM = 'addEvent';

/** Calendar link that opens Add Event on a day, optionally with the customer chosen. */
export const getAddEventLink = ({ date, _customer }: { date?: string; _customer?: string }) => {
	const params = new URLSearchParams({
		date: date || moment().format('YYYY-MM-DD'),
		[ADD_EVENT_PARAM]: 'event',
	});
	if (_customer) params.set('_customer', _customer);
	return `/work/calendar?${params.toString()}`;
};

interface LinkedCustomer {
	_id: string;
	name?: string | null;
	phone?: string | null;
	email?: string | null;
}

/**
 * Reads a link made by getAddEventLink. `isReady` turns true once the
 * customer has loaded (or failed to), and `event` pre-fills the form.
 */
export function useAddEventLink() {
	const [searchParams, setSearchParams] = useSearchParams();
	const isLinked = searchParams.get(ADD_EVENT_PARAM) === 'event';
	const _customer = (isLinked && searchParams.get('_customer')) || undefined;
	const dateParam = searchParams.get('date');

	const { data, isFetching } = useGetCustomerByIdQuery(_customer, { skip: !_customer });
	const customer: LinkedCustomer | undefined = _customer
		? data?.entities?.[_customer]
		: undefined;

	const date = moment(dateParam, 'YYYY-MM-DD', true);
	const day = date.isValid() ? date.toDate() : undefined;

	const event: Partial<CalendarEventType> | undefined = isLinked
		? {
				...(day && { startDate: day, endDate: day }),
				...(customer && {
					job: {
						_id: '',
						_project: '',
						_assignees: [],
						// Only the details the form shows; empty ones are left out
						customer: {
							_id: customer._id,
							name: customer.name || undefined,
							phone: customer.phone || undefined,
							email: customer.email || undefined,
						},
					},
				}),
			}
		: undefined;

	// Drop the link once used, so a refresh or the Add Event button starts fresh
	const clearLink = useCallback(() => {
		setSearchParams(
			(params) => {
				params.delete(ADD_EVENT_PARAM);
				params.delete('_customer');
				return params;
			},
			{ replace: true }
		);
	}, [setSearchParams]);

	return { isReady: isLinked && !isFetching, event, clearLink };
}
